import { useState, useRef, forwardRef, useCallback, useEffect } from 'react'
import { useRouter } from 'next/router'
import dayjs from 'dayjs'
import { useFrame, ThreeEvent, extend } from '@react-three/fiber'
import { useCursor, Text, shaderMaterial } from '@react-three/drei'
import * as THREE from 'three'
import { R3FImage, R3FVideo } from '@/components'
import textFragmentShader from '@/shaders/textFragment.frag'
import vertexShader from '@/shaders/vertex.vert'
import { getTranslatedField } from '@/libs/utils'
import { useScreenSize } from '@/hooks'
import { ArtworkType } from '@/@types/microcms-type'

const TextMaterialImpl = shaderMaterial(
    { color: new THREE.Color('white'), scale: [1, 1], opacity: 1, uSpeed: 0 },
    vertexShader,
    textFragmentShader
)

type TextMaterialType = Omit<JSX.IntrinsicElements['mesh'], 'scale'> &
    JSX.IntrinsicElements['shaderMaterial'] & {
        color?: THREE.Color
        scale?: [number, number]
        opacity?: number
        uSpeed?: THREE.Vector2
    }

declare global {
    namespace JSX {
        interface IntrinsicElements {
            textMaterial: TextMaterialType
        }
    }
}

type ThumbnailProps = {
    project: ArtworkType
    isVideo: boolean
    url: string
    posterUrl?: string
    position: [number, number, number]
    scale: [number, number]
    shouldLoad?: boolean
    props?: any
}

export const Thumbnail = ({
    project,
    isVideo,
    url,
    posterUrl,
    position,
    scale,
    shouldLoad = false,
    ...props
}: ThumbnailProps) => {
    extend({ TextMaterial: TextMaterialImpl })

    const screenSize = useScreenSize()

    const [hovered, setHovered] = useState<boolean>(false)
    const [isLoaded, setIsLoaded] = useState<boolean>(false)
    useCursor(hovered)

    const ref = useRef<THREE.Mesh>(null)

    const updateColorValue = (target: any) => {
        if (screenSize.XS || !shouldLoad) return
        if (!target.material.uniforms) return
        target.material.uniforms.color.value = new THREE.Color(
            target.material.uniforms.color.value.lerp(
                new THREE.Color(hovered ? '#000080' : '#ffffff'),
                0.2
            )
        )
    }

    useFrame(() => {
        if (ref.current && shouldLoad) {
            updateColorValue(ref.current)
        }
    })

    const router = useRouter()
    const handleAnchorClick = (e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation()
        router.push(`/project/${project.id}`)
    }

    const handleAnchorHover = () => {
        setHovered(true)
        router.prefetch(`/project/${project.id}`)
    }

    const [titleHeight, setTitleHeight] = useState<number>(0.16)
    const [clientHeight, setClientHeight] = useState<number>(0.16)

    const titleCallbackRef = useCallback((ref: any) => {
        if (ref) {
            ref.addEventListener('synccomplete', () => {
                const blockBounds = ref.textRenderInfo.blockBounds
                setTitleHeight(blockBounds[3] - blockBounds[1])
            })
        }
    }, [])

    const clientCallbackRef = useCallback((ref: any) => {
        if (ref) {
            ref.addEventListener('synccomplete', () => {
                const blockBounds = ref.textRenderInfo.blockBounds
                setClientHeight(blockBounds[3] - blockBounds[1])
            })
        }
    }, [])

    useEffect(() => {
        if (shouldLoad && !isLoaded) {
            const timer = setTimeout(() => {
                setIsLoaded(true)
            }, 50)
            return () => clearTimeout(timer)
        }
    }, [shouldLoad, isLoaded])

    return (
        <group
            onPointerOver={handleAnchorHover}
            onPointerOut={() => setHovered(false)}
            onClick={(e) => handleAnchorClick(e)}
        >
            {shouldLoad && isLoaded ? (
                <>
                    {isVideo ? (
                        <R3FVideo
                            ref={ref}
                            url={url}
                            segments={screenSize.XS ? 16 : 20}
                            scale={scale}
                            position={position}
                            poster={posterUrl}
                            {...props}
                        />
                    ) : (
                        <R3FImage
                            ref={ref}
                            url={url}
                            segments={screenSize.XS ? 16 : 20}
                            scale={scale}
                            position={position}
                            {...props}
                        />
                    )}
                </>
            ) : (
                <mesh
                    ref={ref}
                    scale={[...scale, 1]}
                    position={position}
                    {...props}
                >
                    <planeGeometry args={[1, 1, 1, screenSize.XS ? 16 : 20]} />
                    <meshBasicMaterial
                        color="#f0f0f0"
                        transparent
                        opacity={0.3}
                    />
                </mesh>
            )}
            <group
                position={
                    screenSize.XS
                        ? [
                              position[0] - scale[0] / 2,
                              position[1] - scale[1] / 2 - 0.1,
                              position[2]
                          ]
                        : [
                              position[0] - scale[0] / 2 + 0.1,
                              position[1] + scale[1] / 2 - 0.2,
                              position[2] + 0.001
                          ]
                }
            >
                {(project.client?.client_ja || project.client?.client_en) && (
                    <ThumbnailText
                        position={[0, 0, 0]}
                        text={getTranslatedField({
                            field: project.client,
                            locale: router.locale
                        })}
                        hovered={hovered}
                        scale={scale}
                        ref={clientCallbackRef}
                    />
                )}
                {(project.project_title?.project_title_ja ||
                    project.project_title?.project_title_en) && (
                    <ThumbnailText
                        position={[0, -clientHeight - 0.05, 0]}
                        text={getTranslatedField({
                            field: project.project_title,
                            locale: router.locale
                        })}
                        hovered={hovered}
                        scale={scale}
                        ref={titleCallbackRef}
                    />
                )}
                {project.date && (
                    <ThumbnailText
                        position={[0, -clientHeight - titleHeight - 0.1, 0]}
                        text={dayjs(project.date).format('YYYY')}
                        hovered={hovered}
                        scale={scale}
                    />
                )}
            </group>
        </group>
    )
}

type ThumbnailTextType = {
    position: [number, number, number]
    text: string | null
    hovered: boolean
    scale: [number, number]
}

const ThumbnailText = forwardRef<THREE.Mesh, ThumbnailTextType>(
    ({ position, text, hovered, scale }, ref) => {
        const screenSize = useScreenSize()

        const fontProps = {
            font: 'A-OTF-GothicBBBPro-Medium.otf',
            fontSize: screenSize.XS ? scale[0] / 28 : scale[0] / 24,
            textAlign: 'left',
            anchorX: 'left',
            anchorY: 'top',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            lineHeight: 1.24,
            letterSpacing: 0.06,
            maxWidth: scale ? ((scale[0] - 0.2) as number) : 1 // subtract margin
        } as const

        return (
            <Text position={position} {...fontProps} ref={ref}>
                {`-${text}`}
                <textMaterial
                    opacity={screenSize.XS ? 1 : hovered ? 1 : 0}
                    color={
                        screenSize.XS
                            ? new THREE.Color('#b5b5b5')
                            : new THREE.Color('#ffffff')
                    }
                />
            </Text>
        )
    }
)
